import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../../../../styles/dashboardTheme.css';
import '../../../../styles/managementTheme.css';
import '../styles/promociones.css';
import {
  crearBloque,
  actualizarBloque,
  eliminarBloque,
  crearBloqueMasivo,
  actualizarAsistenciaBloque,
  actualizarEstadoSesion,
  listarMateriasCronograma,
  obtenerCronograma,
  descargarCronogramaPDF,
  descargarCronogramaExcel,
} from '../../../../services/sesionBloquesService';
import { showToast } from '../../../../utils/showToast';
import { usePermissions } from '../../../../utils/permissions';
import InvitacionDocentesModal from '../components/InvitacionDocentesModal.jsx';
import CalendarioNoLaborableCalendarModal from '../../calendario/components/CalendarioNoLaborableCalendarModal.jsx';

// ── Constantes ────────────────────────────────────────────────────────────────
const PX_POR_MIN   = 1.14;          // altura en px por cada minuto
const BASE_COL_W   = 86;           // ancho base de cada columna de día (px)
const MIN_COL_W    = 43;           // ancho mínimo de columna para evitar scroll
const TIME_COL_W   = 48;          // ancho de la columna de hora (px)
const COL_GAP      = 2;            // separación entre columnas (px)
const LABEL_EACH   = 60;           // mostrar etiqueta de hora cada N minutos

const PALETTE = [
  '#2E86AB','#A23B72','#F18F01','#C73E1D','#3B1F2B',
  '#44BBA4','#E94F37','#393E41','#F5A623','#7BC67E',
  '#9B59B6','#1ABC9C',
];

const TIPOS_BLOQUE = ['clase','receso','libre','feriado','evaluacion','otro'];

const DIAS_CORTO  = ['DOM','LUN','MAR','MIÉ','JUE','VIE','SÁB'];
const MESES_CORTO = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];

// ── Utilidades de tiempo ──────────────────────────────────────────────────────
function timeToMin(t) {
  if (!t) return 0;
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
}
function minToTime(m) {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
function parseFecha(v) {
  return typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10);
}
function labelDia(iso) {
  const d = new Date(iso + 'T12:00:00');
  return { dia: DIAS_CORTO[d.getDay()], num: String(d.getDate()).padStart(2, '0'), mes: MESES_CORTO[d.getMonth()] };
}
function esBgOscuro(hex) {
  const c = (hex || '#aaa').replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 145;
}

// ── Modal Bloque ──────────────────────────────────────────────────────────────
function BloqueModal({ open, bloque, sesion, materias, horasProgramadasPorMateria = {}, onClose, onGuardar, onEliminar }) {
  const isEdit = !!bloque?.bloque_id;
  const [form, setForm] = useState({ tipo: 'clase', modalidad: 'presencial', materia_id: '', docente_id: '', hora_inicio: '', hora_fin: '', titulo: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        tipo:        bloque.tipo || 'clase',
        modalidad:   bloque.modalidad || 'presencial',
        materia_id:  bloque.materia_id ? String(bloque.materia_id) : '',
        docente_id:  bloque.docente_id ? String(bloque.docente_id) : '',
        hora_inicio: bloque.hora_inicio?.slice(0, 5) || '',
        hora_fin:    bloque.hora_fin?.slice(0, 5) || '',
        titulo:      bloque.titulo || '',
      });
    } else {
      setForm({
        tipo:        'clase',
        modalidad:   'presencial',
        materia_id:  '',
        docente_id:  '',
        hora_inicio: bloque?.hora_inicio || '',
        hora_fin:    bloque?.hora_fin || '',
        titulo:      '',
      });
    }
  }, [open, bloque, isEdit]);

  useEffect(() => {
    if (open) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const materiaSeleccionada = materias.find((m) => String(m.id) === String(form.materia_id));

  // Pool de docentes que pueden dictar esta materia: el preferente de la malla
  // + los adicionales calificados (cursos_materias_docentes), sin duplicar.
  const docentesDisponibles = useMemo(() => {
    if (!materiaSeleccionada) return [];
    const lista = [];
    if (materiaSeleccionada.docente_id) {
      lista.push({
        id: materiaSeleccionada.docente_id,
        nombre: `${materiaSeleccionada.docente_nombres || ''} ${materiaSeleccionada.docente_apellidos || ''}`.trim(),
        preferente: true,
      });
    }
    (materiaSeleccionada.docentes_calificados || []).forEach((d) => {
      if (!lista.some((x) => String(x.id) === String(d.id))) {
        lista.push({ id: d.id, nombre: d.nombre, preferente: false });
      }
    });
    return lista;
  }, [materiaSeleccionada]);

  // Aviso de horas: cuánto lleva ya programado esta materia en el cronograma
  // (sin contar el propio bloque si se está editando) vs. lo definido en malla.
  const avisoHoras = useMemo(() => {
    if (!materiaSeleccionada || materiaSeleccionada.horas_malla == null) return null;
    const inicio = timeToMin(form.hora_inicio);
    const fin = timeToMin(form.hora_fin);
    if (!form.hora_inicio || !form.hora_fin || fin <= inicio) return null;

    let base = horasProgramadasPorMateria[materiaSeleccionada.id] || 0;
    if (isEdit && String(bloque.materia_id) === String(materiaSeleccionada.id)) {
      base -= Math.max(0, (timeToMin(bloque.hora_fin) - timeToMin(bloque.hora_inicio)) / 60);
    }
    const nuevas = (fin - inicio) / 60;
    const total = Math.round((base + nuevas) * 10) / 10;
    const malla = materiaSeleccionada.horas_malla;
    const estado = total > malla ? 'excede' : total === malla ? 'completo' : 'normal';
    return { total, malla, estado, excede: estado === 'excede' };
  }, [materiaSeleccionada, form.hora_inicio, form.hora_fin, horasProgramadasPorMateria, isEdit, bloque]);

  // Al cambiar de materia, resetea el docente al preferente (o al único
  // calificado disponible) en vez de dejar seleccionado el de la materia anterior.
  const handleMateriaChange = (materiaId) => {
    const mat = materias.find((m) => String(m.id) === String(materiaId));
    const sugerido = mat?.docente_id ? String(mat.docente_id) : (mat?.docentes_calificados?.[0]?.id ? String(mat.docentes_calificados[0].id) : '');
    setForm((p) => ({ ...p, materia_id: materiaId, docente_id: sugerido }));
  };

  // El bloque no puede salirse del horario de la sesión (ej. si la sesión es
  // 08:00–14:00, no se puede crear un bloque de 12:00 a 16:00).
  const fueraDeSesion = sesion && form.hora_inicio && form.hora_fin
    ? (timeToMin(form.hora_inicio) < timeToMin(sesion.sesion_inicio) || timeToMin(form.hora_fin) > timeToMin(sesion.sesion_fin))
    : false;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.hora_inicio || !form.hora_fin) {
      showToast({ type: 'warning', message: 'Hora inicio y fin requeridas.' });
      return;
    }
    if (timeToMin(form.hora_fin) <= timeToMin(form.hora_inicio)) {
      showToast({ type: 'warning', message: 'La hora fin debe ser posterior a la de inicio.' });
      return;
    }
    if (fueraDeSesion) {
      showToast({ type: 'danger', message: `El bloque debe estar dentro del horario de la sesión (${sesion.sesion_inicio?.slice(0,5)} – ${sesion.sesion_fin?.slice(0,5)}).` });
      return;
    }
    if (avisoHoras?.estado === 'excede') {
      showToast({ type: 'danger', message: `No se puede guardar: esta materia sumaría ${avisoHoras.total}h, superando las ${avisoHoras.malla}h definidas en la malla.` });
      return;
    }
    setSaving(true);
    try {
      await onGuardar({
        tipo:        form.tipo,
        modalidad:   form.modalidad,
        materia_id:  form.materia_id || null,
        docente_id:  form.docente_id || null,
        hora_inicio: form.hora_inicio,
        hora_fin:    form.hora_fin,
        titulo:      form.titulo || null,
      }, isEdit ? bloque.bloque_id : null);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(15,23,42,0.5)', zIndex: 1055 }} tabIndex="-1">
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title">{isEdit ? 'Editar bloque' : 'Nuevo bloque'}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {sesion && (
                <div className="alert alert-light py-2 mb-3 small">
                  <i className="bi bi-calendar2 me-1 text-primary" />
                  <strong>{sesion.fecha}</strong>
                  {' — '}sesión {sesion.sesion_inicio?.slice(0,5)} a {sesion.sesion_fin?.slice(0,5)}
                </div>
              )}
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label management-label">Hora inicio *</label>
                  <input type="time" className={`form-control ${fueraDeSesion ? 'is-invalid' : ''}`} value={form.hora_inicio}
                    min={sesion?.sesion_inicio?.slice(0,5)} max={sesion?.sesion_fin?.slice(0,5)}
                    onChange={(e) => set('hora_inicio', e.target.value)} required />
                </div>
                <div className="col-6">
                  <label className="form-label management-label">Hora fin *</label>
                  <input type="time" className={`form-control ${fueraDeSesion ? 'is-invalid' : ''}`} value={form.hora_fin}
                    min={sesion?.sesion_inicio?.slice(0,5)} max={sesion?.sesion_fin?.slice(0,5)}
                    onChange={(e) => set('hora_fin', e.target.value)} required />
                </div>
                {fueraDeSesion && (
                  <div className="col-12">
                    <div className="alert alert-danger py-2 mb-0 small">
                      <i className="bi bi-exclamation-triangle-fill me-1" />
                      El bloque debe estar dentro del horario de la sesión ({sesion.sesion_inicio?.slice(0,5)} – {sesion.sesion_fin?.slice(0,5)}).
                    </div>
                  </div>
                )}
                <div className="col-6">
                  <label className="form-label management-label">Tipo *</label>
                  <select className="form-select" value={form.tipo} onChange={(e) => set('tipo', e.target.value)} required>
                    {TIPOS_BLOQUE.map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label management-label">Modalidad</label>
                  <div className="btn-group w-100" role="group">
                    <input type="radio" className="btn-check" name="modalidad-bloque" id="mod-presencial"
                      value="presencial" checked={form.modalidad === 'presencial'}
                      onChange={() => set('modalidad', 'presencial')} />
                    <label className="btn btn-outline-secondary btn-sm" htmlFor="mod-presencial">
                      <i className="bi bi-people me-1" />Presencial
                    </label>
                    <input type="radio" className="btn-check" name="modalidad-bloque" id="mod-online"
                      value="online" checked={form.modalidad === 'online'}
                      onChange={() => set('modalidad', 'online')} />
                    <label className="btn btn-outline-info btn-sm" htmlFor="mod-online">
                      <i className="bi bi-camera-video me-1" />Online
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <label className="form-label management-label">Materia</label>
                  <select className="form-select" value={form.materia_id}
                    onChange={(e) => handleMateriaChange(e.target.value)}>
                    <option value="">— Sin materia —</option>
                    {materias.map((m) => <option key={m.id} value={m.id}>{m.nombre}{m.variante_codigo ? ` (${m.variante_codigo})` : ""}</option>)}
                  </select>
                </div>
                {form.materia_id && docentesDisponibles.length > 0 && (
                  <div className="col-12">
                    <label className="form-label management-label">Docente</label>
                    <select className="form-select" value={form.docente_id}
                      onChange={(e) => set('docente_id', e.target.value)}>
                      {docentesDisponibles.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.nombre || `Docente #${d.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {form.materia_id && docentesDisponibles.length === 0 && (
                  <div className="col-12">
                    <div className="alert alert-warning py-2 mb-0 small d-flex align-items-center justify-content-between gap-2">
                      <span>
                        <i className="bi bi-exclamation-triangle me-1" />
                        Esta materia no tiene ningún docente calificado en la malla curricular.
                      </span>
                      <a
                        href="/dashboard/coordinacion/malla"
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary flex-shrink-0"
                      >
                        Ir a Malla Curricular
                      </a>
                    </div>
                  </div>
                )}
                {avisoHoras && (
                  <div className="col-12">
                    <div className={`alert py-2 mb-0 small ${
                      avisoHoras.estado === 'excede' ? 'alert-danger' : avisoHoras.estado === 'completo' ? 'alert-success' : 'alert-secondary'
                    }`}>
                      <i className={`bi ${
                        avisoHoras.estado === 'excede' ? 'bi-exclamation-triangle-fill' : avisoHoras.estado === 'completo' ? 'bi-check-circle-fill' : 'bi-info-circle'
                      } me-1`} />
                      {avisoHoras.estado === 'excede'
                        ? `No se puede guardar: con este bloque, esta materia sumaría ${avisoHoras.total}h, superando las ${avisoHoras.malla}h definidas en la malla. Reduce la hora o ajusta la malla.`
                        : avisoHoras.estado === 'completo'
                          ? `Con este bloque, esta materia completa exactamente las ${avisoHoras.malla}h definidas en la malla.`
                          : `Con este bloque, esta materia sumaría ${avisoHoras.total}h de ${avisoHoras.malla}h definidas en la malla.`}
                    </div>
                  </div>
                )}
                {form.tipo !== 'clase' && (
                  <div className="col-12">
                    <label className="form-label management-label">Descripción</label>
                    <input className="form-control" value={form.titulo}
                      onChange={(e) => set('titulo', e.target.value)}
                      placeholder="Ej: Receso, Evaluación..." />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              {isEdit && (
                <button type="button" className="btn btn-outline-danger btn-sm me-auto"
                  onClick={() => { onEliminar(bloque.bloque_id); onClose(); }}>
                  <i className="bi bi-trash me-1" />Eliminar
                </button>
              )}
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn management-btn-primary" disabled={saving || fueraDeSesion || avisoHoras?.estado === 'excede'}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Modal Asignación Masiva ───────────────────────────────────────────────────
function MasivoModal({ open, sesiones, materias, horasProgramadasPorMateria = {}, onClose, onGuardar }) {
  const [form, setForm] = useState({
    materia_id: '', docente_id: '', hora_inicio: '', hora_fin: '', tipo: 'clase', modalidad: 'presencial', titulo: '',
  });
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ materia_id: '', docente_id: '', hora_inicio: '', hora_fin: '', tipo: 'clase', modalidad: 'presencial', titulo: '' });
      setSeleccionadas([]);
    }
  }, [open]);

  useEffect(() => {
    if (open) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const materiaSeleccionada = materias.find((m) => String(m.id) === String(form.materia_id));

  // Duración (h) del bloque que se va a repetir en cada sesión marcada, y el
  // límite de la malla para esta materia (si está definido). Se usan tanto
  // para el aviso como para bloquear el propio checkbox de la sesión.
  const duracionHoras = useMemo(() => {
    const inicio = timeToMin(form.hora_inicio);
    const fin = timeToMin(form.hora_fin);
    if (!form.hora_inicio || !form.hora_fin || fin <= inicio) return 0;
    return (fin - inicio) / 60;
  }, [form.hora_inicio, form.hora_fin]);

  const limiteHoras = materiaSeleccionada?.horas_malla ?? null;
  const baseHoras = materiaSeleccionada ? (horasProgramadasPorMateria[materiaSeleccionada.id] || 0) : 0;

  // El bloque se repite tal cual en cada sesión marcada, así que debe caber
  // dentro del horario de TODAS ellas (si aún no se marca ninguna, se usa el
  // horario de todas las sesiones de la lista como referencia).
  const ventana = useMemo(() => {
    const relevantes = seleccionadas.length
      ? sesiones.filter((s) => seleccionadas.includes(s.sesion_id))
      : sesiones;
    if (!relevantes.length) return null;
    const inicio = Math.max(...relevantes.map((s) => timeToMin(s.sesion_inicio)));
    const fin = Math.min(...relevantes.map((s) => timeToMin(s.sesion_fin)));
    return { inicio, fin, inicioTxt: minToTime(inicio), finTxt: minToTime(fin) };
  }, [sesiones, seleccionadas]);

  const fueraDeVentana = ventana && form.hora_inicio && form.hora_fin
    ? (timeToMin(form.hora_inicio) < ventana.inicio || timeToMin(form.hora_fin) > ventana.fin)
    : false;

  // Si marcar esta sesión (una más) haría que la materia supere las horas de
  // la malla, no se permite marcarla — una ya marcada siempre se puede
  // desmarcar.
  const puedeMarcar = (sesionId) => {
    if (seleccionadas.includes(sesionId)) return true;
    if (limiteHoras == null || !duracionHoras) return true;
    const proyectado = Math.round((baseHoras + duracionHoras * (seleccionadas.length + 1)) * 10) / 10;
    return proyectado <= limiteHoras;
  };

  const toggleSesion = (id) => {
    const checked = seleccionadas.includes(id);
    if (!checked && !puedeMarcar(id)) {
      showToast({ type: 'warning', message: `No se puede marcar esta sesión: esta materia superaría las ${limiteHoras}h definidas en la malla.` });
      return;
    }
    setSeleccionadas((prev) => (checked ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleTodas = () => {
    if (seleccionadas.length === sesiones.length) {
      setSeleccionadas([]);
      return;
    }
    if (limiteHoras == null || !duracionHoras) {
      setSeleccionadas(sesiones.map((s) => s.sesion_id));
      return;
    }
    const maxSesiones = Math.max(0, Math.floor((limiteHoras - baseHoras) / duracionHoras + 1e-9));
    const permitidas = sesiones.slice(0, maxSesiones).map((s) => s.sesion_id);
    setSeleccionadas(permitidas);
    if (permitidas.length < sesiones.length) {
      showToast({ type: 'warning', message: `Solo se marcaron ${permitidas.length} de ${sesiones.length} sesiones: más superaría las ${limiteHoras}h definidas en la malla.` });
    }
  };

  const docentesDisponibles = useMemo(() => {
    if (!materiaSeleccionada) return [];
    const lista = [];
    if (materiaSeleccionada.docente_id) {
      lista.push({
        id: materiaSeleccionada.docente_id,
        nombre: `${materiaSeleccionada.docente_nombres || ''} ${materiaSeleccionada.docente_apellidos || ''}`.trim(),
        preferente: true,
      });
    }
    (materiaSeleccionada.docentes_calificados || []).forEach((d) => {
      if (!lista.some((x) => String(x.id) === String(d.id))) {
        lista.push({ id: d.id, nombre: d.nombre, preferente: false });
      }
    });
    return lista;
  }, [materiaSeleccionada]);

  const handleMateriaChange = (materiaId) => {
    const mat = materias.find((m) => String(m.id) === String(materiaId));
    const sugerido = mat?.docente_id ? String(mat.docente_id) : (mat?.docentes_calificados?.[0]?.id ? String(mat.docentes_calificados[0].id) : '');
    setForm((p) => ({ ...p, materia_id: materiaId, docente_id: sugerido }));
  };

  // Aviso de horas: la asignación masiva repite el MISMO bloque en cada sesión
  // marcada, así que las horas nuevas son duración × cantidad de sesiones.
  const avisoHoras = useMemo(() => {
    if (!materiaSeleccionada || limiteHoras == null || !duracionHoras || !seleccionadas.length) return null;

    const total = Math.round((baseHoras + duracionHoras * seleccionadas.length) * 10) / 10;
    const malla = limiteHoras;
    const estado = total > malla ? 'excede' : total === malla ? 'completo' : 'normal';
    return { total, malla, estado, excede: estado === 'excede' };
  }, [materiaSeleccionada, limiteHoras, duracionHoras, baseHoras, seleccionadas.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!seleccionadas.length) {
      showToast({ type: 'warning', message: 'Selecciona al menos una sesión.' });
      return;
    }
    if (!form.hora_inicio || !form.hora_fin) {
      showToast({ type: 'warning', message: 'Define el horario del bloque.' });
      return;
    }
    if (fueraDeVentana) {
      showToast({ type: 'danger', message: `El bloque debe estar dentro del horario de las sesiones marcadas (${ventana.inicioTxt} – ${ventana.finTxt}).` });
      return;
    }
    if (avisoHoras?.estado === 'excede') {
      showToast({ type: 'danger', message: `No se puede guardar: esta materia sumaría ${avisoHoras.total}h, superando las ${avisoHoras.malla}h definidas en la malla.` });
      return;
    }
    setSaving(true);
    try {
      await onGuardar({
        sesion_ids:  seleccionadas,
        tipo:        form.tipo,
        modalidad:   form.modalidad,
        materia_id:  form.materia_id || null,
        docente_id:  form.docente_id || null,
        hora_inicio: form.hora_inicio,
        hora_fin:    form.hora_fin,
        titulo:      form.titulo || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(15,23,42,0.5)', zIndex: 1055 }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        {/* maxHeight + flex column explícitos: el footer con los botones debe
            quedar siempre fijo y visible, sin depender solo de que ninguna
            regla global rompa el layout scrollable de Bootstrap. */}
        <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header border-0 pb-0" style={{ flex: '0 0 auto' }}>
            <div>
              <h5 className="modal-title mb-0">
                <i className="bi bi-lightning-charge-fill text-warning me-2" />
                Asignación masiva de bloques
              </h5>
              <p className="text-muted small mb-0 mt-1">Define un bloque horario y selecciona en qué sesiones aplicarlo.</p>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto' }}>
            <div className="modal-body" style={{ overflowY: 'auto', minHeight: 0, flex: '1 1 auto' }}>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label management-label">Materia</label>
                  <select className="form-select" value={form.materia_id}
                    onChange={(e) => handleMateriaChange(e.target.value)}>
                    <option value="">— Sin materia (receso, etc.) —</option>
                    {materias.map((m) => <option key={m.id} value={m.id}>{m.nombre}{m.variante_codigo ? ` (${m.variante_codigo})` : ""}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label management-label">Tipo</label>
                  <select className="form-select" value={form.tipo} onChange={(e) => set('tipo', e.target.value)}>
                    {['clase','receso','libre','feriado','evaluacion','otro'].map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label management-label">Modalidad</label>
                  <div className="btn-group w-100" role="group">
                    <input type="radio" className="btn-check" name="modalidad-masivo" id="masivo-presencial"
                      value="presencial" checked={form.modalidad === 'presencial'}
                      onChange={() => set('modalidad', 'presencial')} />
                    <label className="btn btn-outline-secondary btn-sm" htmlFor="masivo-presencial">
                      <i className="bi bi-people me-1" />Presencial
                    </label>
                    <input type="radio" className="btn-check" name="modalidad-masivo" id="masivo-online"
                      value="online" checked={form.modalidad === 'online'}
                      onChange={() => set('modalidad', 'online')} />
                    <label className="btn btn-outline-info btn-sm" htmlFor="masivo-online">
                      <i className="bi bi-camera-video me-1" />Online
                    </label>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label management-label">Hora inicio *</label>
                  <input type="time" className={`form-control ${fueraDeVentana ? 'is-invalid' : ''}`} value={form.hora_inicio}
                    min={ventana?.inicioTxt} max={ventana?.finTxt}
                    onChange={(e) => set('hora_inicio', e.target.value)} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label management-label">Hora fin *</label>
                  <input type="time" className={`form-control ${fueraDeVentana ? 'is-invalid' : ''}`} value={form.hora_fin}
                    min={ventana?.inicioTxt} max={ventana?.finTxt}
                    onChange={(e) => set('hora_fin', e.target.value)} required />
                </div>
                {fueraDeVentana && (
                  <div className="col-12">
                    <div className="alert alert-danger py-2 mb-0 small">
                      <i className="bi bi-exclamation-triangle-fill me-1" />
                      El bloque debe estar dentro del horario de {seleccionadas.length ? 'las sesiones marcadas' : 'las sesiones'} ({ventana.inicioTxt} – {ventana.finTxt}).
                    </div>
                  </div>
                )}
                {form.tipo !== 'clase' && (
                  <div className="col-12">
                    <label className="form-label management-label">Descripción</label>
                    <input className="form-control" value={form.titulo}
                      onChange={(e) => set('titulo', e.target.value)}
                      placeholder="Ej: Receso, Evaluación parcial..." />
                  </div>
                )}
                {form.materia_id && docentesDisponibles.length > 0 && (
                  <div className="col-12">
                    <label className="form-label management-label">Docente</label>
                    <select className="form-select" value={form.docente_id}
                      onChange={(e) => set('docente_id', e.target.value)}>
                      {docentesDisponibles.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.nombre || `Docente #${d.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {form.materia_id && docentesDisponibles.length === 0 && (
                  <div className="col-12">
                    <div className="alert alert-warning py-2 mb-0 small d-flex align-items-center justify-content-between gap-2">
                      <span>
                        <i className="bi bi-exclamation-triangle me-1" />
                        Esta materia no tiene ningún docente calificado en la malla curricular.
                      </span>
                      <a
                        href="/dashboard/coordinacion/malla"
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary flex-shrink-0"
                      >
                        Ir a Malla Curricular
                      </a>
                    </div>
                  </div>
                )}
                {avisoHoras && (
                  <div className="col-12">
                    <div className={`alert py-2 mb-0 small ${
                      avisoHoras.estado === 'excede' ? 'alert-danger' : avisoHoras.estado === 'completo' ? 'alert-success' : 'alert-secondary'
                    }`}>
                      <i className={`bi ${
                        avisoHoras.estado === 'excede' ? 'bi-exclamation-triangle-fill' : avisoHoras.estado === 'completo' ? 'bi-check-circle-fill' : 'bi-info-circle'
                      } me-1`} />
                      {avisoHoras.estado === 'excede'
                        ? `No se puede guardar: con las ${seleccionadas.length} sesión(es) marcadas, esta materia sumaría ${avisoHoras.total}h, superando las ${avisoHoras.malla}h definidas en la malla.`
                        : avisoHoras.estado === 'completo'
                          ? `Con las ${seleccionadas.length} sesión(es) marcadas, esta materia completa exactamente las ${avisoHoras.malla}h definidas en la malla.`
                          : `Con las ${seleccionadas.length} sesión(es) marcadas, esta materia sumaría ${avisoHoras.total}h de ${avisoHoras.malla}h definidas en la malla.`}
                    </div>
                  </div>
                )}
              </div>

              {/* Selección de sesiones */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label management-label mb-0">
                  Sesiones donde aplicar ({seleccionadas.length} de {sesiones.length})
                </label>
                <button type="button" className="btn btn-outline-secondary btn-sm"
                  onClick={toggleTodas}>
                  {seleccionadas.length === sesiones.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                </button>
              </div>
              <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                <table className="table table-sm align-middle mb-0">
                  <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Fecha</th>
                      <th>Horario sesión</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sesiones.map((s) => {
                      const checked = seleccionadas.includes(s.sesion_id);
                      const bloqueada = !checked && !puedeMarcar(s.sesion_id);
                      return (
                        <tr key={s.sesion_id}
                          title={bloqueada ? `Superaría las ${limiteHoras}h definidas en la malla` : undefined}
                          style={{
                            cursor: bloqueada ? 'not-allowed' : 'pointer',
                            background: checked ? '#eef4ff' : undefined,
                            opacity: bloqueada ? 0.5 : 1,
                          }}
                          onClick={() => toggleSesion(s.sesion_id)}>
                          <td>
                            <input type="checkbox" className="form-check-input" readOnly checked={checked} disabled={bloqueada} />
                          </td>
                          <td className="fw-semibold">{s.fecha}</td>
                          <td>{s.sesion_inicio?.slice(0,5)} – {s.sesion_fin?.slice(0,5)}</td>
                          <td>
                            <span className={`badge rounded-pill ${
                              s.sesion_estado === 'realizada'    ? 'bg-success-subtle text-success' :
                              s.sesion_estado === 'suspendida'   ? 'bg-danger-subtle text-danger' :
                              s.sesion_estado === 'reprogramada' ? 'bg-warning-subtle text-warning' :
                              'bg-primary-subtle text-primary'
                            }`}>{s.sesion_estado}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0" style={{ flex: '0 0 auto' }}>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn management-btn-primary" disabled={saving || !seleccionadas.length || fueraDeVentana || avisoHoras?.estado === 'excede'}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" />Aplicando...</>
                  : <><i className="bi bi-lightning-charge me-1" />Aplicar a {seleccionadas.length} sesión{seleccionadas.length !== 1 ? 'es' : ''}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── CalendarioGrid ────────────────────────────────────────────────────────────
function CalendarioGrid({
  sesiones,
  colores,
  minHora,
  maxHora,
  canEdit,
  onClickVacio,
  onClickBloque,
  onAsistencia,
  colWidth,
  timeColW,
  colGap,
  pxPorMin,
}) {
  const totalMin  = timeToMin(maxHora) - timeToMin(minHora);
  const totalAlto = totalMin * pxPorMin;

  // Etiquetas de hora cada LABEL_EACH minutos
  const etiquetas = [];
  for (let m = 0; m <= totalMin; m += LABEL_EACH) {
    etiquetas.push({ min: m, label: minToTime(timeToMin(minHora) + m) });
  }

  const handleClickColumna = (sesion, e) => {
    if (!canEdit) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const clickMin = Math.floor(clickY / pxPorMin / 60) * 60; // redondear a 60 min
    const inicioAbsoluto = timeToMin(minHora) + clickMin;
    const hora_inicio = minToTime(Math.max(inicioAbsoluto, timeToMin(sesion.sesion_inicio)));
    const hora_fin    = minToTime(Math.min(inicioAbsoluto + 60, timeToMin(sesion.sesion_fin)));
    onClickVacio(sesion, hora_inicio, hora_fin);
  };

  return (
    <div style={{ display: 'flex', userSelect: 'none' }}>
      {/* Columna de horas */}
      <div style={{ width: timeColW, flexShrink: 0, position: 'relative', height: totalAlto }}>
        {etiquetas.map(({ min, label }) => (
          <div key={min} style={{
            position: 'absolute',
            top: min * pxPorMin - 8,
            right: 8,
            fontSize: 10,
            color: '#666',
            whiteSpace: 'nowrap',
          }}>
            {label}
          </div>
        ))}
      </div>

      {/* Líneas horizontales de guía */}
      <div style={{ position: 'relative', flex: 1, display: 'flex', gap: colGap }}>
        {/* Líneas de hora (overlay) */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: totalAlto,
          pointerEvents: 'none', zIndex: 2,
        }}>
          {etiquetas.map(({ min }) => (
            <div key={min} style={{
              position: 'absolute',
              top: min * pxPorMin,
              left: 0, right: 0,
              borderTop: '1px solid #d1d5db',
            }} />
          ))}
        </div>

        {/* Columnas de sesión */}
        {sesiones.map((sesion) => {
          const sesIniMin  = timeToMin(sesion.sesion_inicio);
          const sesFinMin  = timeToMin(sesion.sesion_fin);
          const sesTop     = (sesIniMin - timeToMin(minHora)) * pxPorMin;
          const sesAlto    = (sesFinMin - sesIniMin) * pxPorMin;
          return (
            <div key={sesion.sesion_id} style={{
              width: colWidth, flexShrink: 0,
              position: 'relative', height: totalAlto,
            }}>
              {/* Zona clicable de la sesión */}
              <div
                style={{
                  position: 'absolute',
                  top: sesTop, height: sesAlto,
                  left: 0, right: 0,
                  background: 'rgba(240, 244, 255, 0.4)',
                  borderRadius: 4,
                  border: '1px solid #d5defe',
                  cursor: canEdit ? 'crosshair' : 'default',
                  zIndex: 1,
                }}
                onClick={(e) => handleClickColumna(sesion, e)}
                title={canEdit ? 'Clic para agregar bloque' : undefined}
              />

              {/* Bloques */}
              {(sesion.bloques || []).map((b) => {
                const bTop    = (timeToMin(b.hora_inicio) - timeToMin(minHora)) * pxPorMin;
                const bAlto   = Math.max((timeToMin(b.hora_fin) - timeToMin(b.hora_inicio)) * pxPorMin, 20);
                const ausente = b.asistio === 0;
                const colorBase = b.materia_id ? (colores[b.materia_id] || '#ccc') : '#94a3b8';
                const isOnline = b.modalidad === 'online';
                const color    = ausente ? '#9ca3af' : colorBase;
                const fg       = ausente ? '#fff' : (esBgOscuro(colorBase) ? '#fff' : '#000');
                const texto   = b.materia_nombre || b.titulo || b.tipo;

                return (
                  <div key={b.bloque_id} style={{
                    position: 'absolute',
                    top: bTop, height: bAlto,
                    left: 3, right: 3,
                    background: ausente
                      ? 'repeating-linear-gradient(45deg,#9ca3af,#9ca3af 4px,#d1d5db 4px,#d1d5db 8px)'
                      : isOnline
                        ? `repeating-linear-gradient(135deg,transparent,transparent 4px,rgba(255,255,255,0.28) 4px,rgba(255,255,255,0.28) 7px),${color}`
                        : color,
                    color: fg,
                    borderRadius: 4,
                    fontSize: 10, fontWeight: 700,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    textAlign: 'center',
                    overflow: 'hidden',
                    zIndex: 3,
                    padding: '2px',
                    boxShadow: ausente ? 'none' : '0 1px 3px rgba(0,0,0,0.2)',
                    opacity: ausente ? 0.75 : 1,
                    border: ausente ? '2px dashed #6b7280' : isOnline ? '2px dashed rgba(255,255,255,0.6)' : 'none',
                  }}>
                    {/* Texto — clic para editar */}
                    <div style={{
                      writingMode: bAlto > 50 ? 'vertical-rl' : 'horizontal-tb',
                      cursor: canEdit ? 'pointer' : 'default',
                      flex: 1, display: 'flex', alignItems: 'center', gap: 2,
                    }}
                      onClick={(e) => { e.stopPropagation(); onClickBloque(sesion, b); }}
                      title={`${texto}\n${b.hora_inicio?.slice(0,5)}–${b.hora_fin?.slice(0,5)}${isOnline ? '\n🌐 Virtual' : '\n👥 Presencial'}${ausente ? '\n⚠ AUSENTE' : ''}`}
                    >
                      {ausente ? `✗ ${texto}` : texto}
                    </div>
                    {/* Indicador de modalidad */}
                    {!ausente && (
                      <span style={{
                        fontSize: 7, fontWeight: 700, letterSpacing: 0.3,
                        background: 'rgba(0,0,0,0.25)', borderRadius: 2,
                        padding: '0 2px', flexShrink: 0, lineHeight: 1.5,
                      }}>
                        {isOnline ? 'VIRTUAL' : 'PRESENCIAL'}
                      </span>
                    )}
                    {/* Botón asistencia — solo si sesión realizada y puede editar */}
                    {canEdit && sesion.sesion_estado === 'realizada' && bAlto > 35 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onAsistencia(b, !ausente ? false : true); }}
                        title={ausente ? 'Marcar que asistió' : 'Marcar como ausente'}
                        style={{
                          fontSize: 8, padding: '1px 3px', lineHeight: 1.3,
                          border: 'none', borderRadius: 2, cursor: 'pointer',
                          background: ausente ? '#dcfce7' : '#fee2e2',
                          color: ausente ? '#166534' : '#991b1b',
                          marginBottom: 1, flexShrink: 0,
                        }}
                      >
                        {ausente ? '+ asistió' : '✗ ausente'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Vista principal ──────────────────────────────────────────────────────────
// Se embebe como pestaña dentro de PromocionInscritosView — recibe el id de la
// promoción por prop en vez de leerlo de la URL, y no trae su propio header
// (breadcrumb/Volver), ya que la página contenedora ya tiene el suyo.
export default function PromocionCronogramaView({ promocionId: id }) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('calendario.crear');
  const scrollRef = useRef(null);

  const [datos,      setDatos]      = useState(null);
  const [materias,   setMaterias]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Modal de bloque
  const [modal,       setModal]       = useState(false);
  const [modalData,   setModalData]   = useState(null);  // { sesion, bloque? }
  const [modalMasivo, setModalMasivo] = useState(false);
  const [warnings,    setWarnings]    = useState([]);    // alertas de conflicto de docente

  const [exportando, setExportando] = useState(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [showFeriadosModal, setShowFeriadosModal] = useState(false);
  const [showInvitacionModal, setShowInvitacionModal] = useState(false);
  const exportDropdownRef = useRef(null);
  const [colWidth, setColWidth] = useState(BASE_COL_W);
  const timeColW = TIME_COL_W;
  const colGap = COL_GAP;
  const pxPorMin = PX_POR_MIN;

  // ── Fetch ────────────────────────────────────────────────────────────────────
  // silent=true: actualiza datos sin ocultar el calendario (para refrescos post-acción)
  const fetchDatos = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const [crono, mats] = await Promise.all([
        obtenerCronograma(id),
        listarMateriasCronograma(id),
      ]);
      setDatos(crono);
      setMaterias(Array.isArray(mats) ? mats : []);
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error al cargar cronograma.' });
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDatos(); }, [fetchDatos]);

  useEffect(() => {
    if (!exportDropdownOpen) return undefined;
    const handleOutside = (e) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [exportDropdownOpen]);

  // ── Colores por materia ──────────────────────────────────────────────────────
  const colores = useMemo(() => {
    const m = {};
    (datos?.materias || []).forEach((mat, i) => {
      m[mat.id] = mat.color_hex || PALETTE[i % PALETTE.length];
    });
    return m;
  }, [datos?.materias]);

  // ── Calcular rango horario global ────────────────────────────────────────────
  const { minHora, maxHora, sesiones } = useMemo(() => {
    const ss = datos?.sesiones || [];
    if (!ss.length) return { minHora: '08:00', maxHora: '18:00', sesiones: [] };

    let minM = Infinity, maxM = -Infinity;
    ss.forEach((s) => {
      minM = Math.min(minM, timeToMin(s.sesion_inicio));
      maxM = Math.max(maxM, timeToMin(s.sesion_fin));
    });

    return {
      minHora: minToTime(minM),
      maxHora: minToTime(maxM),
      sesiones: ss,
    };
  }, [datos?.sesiones]);

  // ── Leyenda ──────────────────────────────────────────────────────────────────
  // Horas ya programadas en el cronograma por materia, para contrastar contra
  // horas_malla (definida en Malla Curricular) y avisar si alguien se pasó.
  const horasProgramadasPorMateria = useMemo(() => {
    const minutos = {};
    for (const s of sesiones) {
      for (const b of (s.bloques || [])) {
        if (!b.materia_id) continue;
        const dur = timeToMin(b.hora_fin) - timeToMin(b.hora_inicio);
        minutos[b.materia_id] = (minutos[b.materia_id] || 0) + Math.max(0, dur);
      }
    }
    const horas = {};
    Object.entries(minutos).forEach(([id, m]) => { horas[id] = Math.round((m / 60) * 10) / 10; });
    return horas;
  }, [sesiones]);

  const horasMallaPorMateria = useMemo(() => {
    const map = {};
    (datos?.materias || []).forEach((m) => { map[m.id] = m.horas_malla ?? null; });
    return map;
  }, [datos?.materias]);

  const leyenda = useMemo(() => {
    const map = {};
    for (const s of sesiones) {
      for (const b of (s.bloques || [])) {
        if (!b.materia_id || map[b.materia_id]) continue;
        map[b.materia_id] = {
          id:     b.materia_id,
          nombre: b.materia_nombre,
          color:  colores[b.materia_id] || '#ccc',
          modalidad: b.modalidad === 'online' ? 'Virtual' : 'Presencial',
          docente: b.docente_nombres
            ? `${b.docente_nombres} ${b.docente_apellidos || ''}`.trim()
            : '—',
        };
      }
    }
    return Object.values(map);
  }, [sesiones, colores]);

  // ── Agrupar sesiones por mes para el encabezado ──────────────────────────────
  const grupos = useMemo(() => {
    const g = [];
    sesiones.forEach((s) => {
      const f   = parseFecha(s.fecha);
      const lbl = labelDia(f);
      const last = g[g.length - 1];
      if (!last || last.mes !== lbl.mes) g.push({ mes: lbl.mes, sesiones: [{ ...s, fecha: f, lbl }] });
      else last.sesiones.push({ ...s, fecha: f, lbl });
    });
    return g;
  }, [sesiones]);

  const sesionesFlat = useMemo(() => grupos.flatMap((g) => g.sesiones), [grupos]);

  // â”€â”€ Ajustar ancho de columnas para ver ~20 dÃ­as sin scroll
  useEffect(() => {
    const el = scrollRef.current;
    const daysRaw = Math.max(1, sesionesFlat.length);
    const days = Math.min(daysRaw, 20); // objetivo: 20 dÃ­as visibles sin scroll

    const update = () => {
      if (!el) return;
      if (el.clientWidth <= 0) return;
      const available = el.clientWidth - TIME_COL_W - COL_GAP * (days - 1) - 2;
      const next = Math.floor(available / days);
      if (!Number.isFinite(next) || next <= 0) return;
      const clamped = Math.max(MIN_COL_W, Math.min(BASE_COL_W, next));
      setColWidth(clamped);
    };

    update();

    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [sesionesFlat.length]);

  // ── Handlers CRUD ────────────────────────────────────────────────────────────
  const abrirNuevo = (sesion, hora_inicio, hora_fin) => {
    setModalData({ sesion, bloque: { hora_inicio, hora_fin } });
    setModal(true);
  };
  const abrirEditar = (sesion, bloque) => {
    setModalData({ sesion, bloque });
    setModal(true);
  };
  const cerrarModal = () => { setModal(false); setModalData(null); };

  const _mostrarWarnings = (ws) => {
    if (ws?.length) {
      setWarnings(ws);
      setTimeout(() => setWarnings([]), 8000);
    }
  };

  const handleGuardar = async (form, bloqueId) => {
    try {
      let res;
      if (bloqueId) {
        res = await actualizarBloque(bloqueId, form);
        showToast({ type: 'success', message: 'Bloque actualizado.' });
      } else {
        res = await crearBloque({ ...form, promocion_sesion_id: modalData.sesion.sesion_id });
        showToast({ type: 'success', message: 'Bloque creado.' });
      }
      _mostrarWarnings(res?.warnings);
      fetchDatos(true);
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error al guardar bloque.' });
      throw e;
    }
  };

  const handleGuardarMasivo = async (form) => {
    try {
      const res = await crearBloqueMasivo(form);
      const { creados, omitidos, conflictos } = res;
      showToast({
        type: creados.length ? 'success' : 'warning',
        message: `${creados.length} bloque(s) creado(s)${omitidos.length ? `, ${omitidos.length} omitido(s)` : ''}.`,
      });
      if (conflictos.length) {
        setWarnings(conflictos.map((c) =>
          `Conflicto en sesión ${c.sesion_id}: docente ya tiene "${c.conflicto?.materia_nombre || 'otro bloque'}" en ${c.conflicto?.codigo_convocatoria || 'otra promoción'} a las ${c.conflicto?.hora_inicio?.slice(0,5)}–${c.conflicto?.hora_fin?.slice(0,5)}`
        ));
        setTimeout(() => setWarnings([]), 10000);
      }
      fetchDatos(true);
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error en asignación masiva.' });
      throw e;
    }
  };

  const handleEliminar = async (bloqueId) => {
    if (!window.confirm('¿Eliminar este bloque?')) return;
    try {
      await eliminarBloque(bloqueId);
      showToast({ type: 'success', message: 'Bloque eliminado.' });
      fetchDatos(true);
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error al eliminar.' });
    }
  };

  const handleAsistencia = async (bloque, asistio) => {
    try {
      await actualizarAsistenciaBloque(bloque.bloque_id, asistio);
      showToast({
        type: asistio ? 'success' : 'warning',
        message: asistio ? 'Marcado: asistió.' : 'Marcado: ausente — horas no se contabilizarán.',
      });
      fetchDatos(true);
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error al actualizar asistencia.' });
    }
  };

  const handleEstadoSesion = async (sesion, nuevoEstado) => {
    try {
      await actualizarEstadoSesion(sesion.sesion_id, nuevoEstado);
      showToast({ type: 'success', message: `Sesión marcada como "${nuevoEstado}".` });
      fetchDatos(true);
    } catch (e) {
      showToast({ type: 'danger', message: e.message || 'Error al actualizar estado.' });
    }
  };

  const handleExportar = async (tipo) => {
    setExportando(tipo);
    try {
      if (tipo === 'pdf') await descargarCronogramaPDF(id);
      else await descargarCronogramaExcel(id);
      showToast({ type: 'success', message: `${tipo.toUpperCase()} descargado.` });
    } catch (e) {
      showToast({ type: 'danger', message: e.message || `Error al generar ${tipo}.` });
    } finally {
      setExportando(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="cronograma-tab">
      {/* Barra de acciones del cronograma (sin header propio — el curso/promocion ya se ve arriba) */}
      <div className="management-card d-flex flex-wrap align-items-center justify-content-end gap-2 mb-4">
        <div className="d-flex flex-column align-items-end gap-2">
          <div className="d-flex gap-2 flex-wrap justify-content-end align-items-center">
            {canEdit && (
              <button className="btn btn-warning btn-sm fw-semibold"
                onClick={() => setModalMasivo(true)}
                title="Asignar una materia a múltiples sesiones de una vez">
                <i className="bi bi-lightning-charge-fill me-1" />Asignación masiva
              </button>
            )}
            <button className="btn btn-outline-success btn-sm"
              onClick={() => setShowInvitacionModal(true)}
              title="Estado y reenvío de la invitación al grupo de WhatsApp para los docentes">
              <i className="bi bi-whatsapp me-1" />Invitación docentes
            </button>
            <button className="btn btn-outline-secondary btn-sm"
              onClick={() => setShowFeriadosModal(true)}
              title="Agregar, editar o quitar feriados y días no laborables">
              <i className="bi bi-calendar2-week me-1" />Feriados
            </button>
            <div className="position-relative" ref={exportDropdownRef}>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setExportDropdownOpen((o) => !o)}
                disabled={!!exportando}
                title="Descargar cronograma"
              >
                {exportando
                  ? <span className="spinner-border spinner-border-sm" role="status" />
                  : <i className="bi bi-download" />}
              </button>
              {exportDropdownOpen && (
                <ul className="dropdown-menu show" style={{ top: '100%', right: 0, left: 'auto', width: 'max-content' }}>
                  <li>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => { setExportDropdownOpen(false); handleExportar('pdf'); }}
                      disabled={exportando === 'pdf'}
                    >
                      <i className="bi bi-file-earmark-pdf text-danger me-2" />
                      PDF
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => { setExportDropdownOpen(false); handleExportar('excel'); }}
                      disabled={exportando === 'excel'}
                    >
                      <i className="bi bi-file-earmark-excel text-success me-2" />
                      Excel
                    </button>
                  </li>
                </ul>
              )}
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => fetchDatos(true)} disabled={loading || refreshing}>
              {refreshing
                ? <span className="spinner-border spinner-border-sm" role="status" />
                : <i className="bi bi-arrow-repeat" />}
            </button>
          </div>          
        </div>
      </div>

      {loading && !datos && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted small">Cargando cronograma...</p>
        </div>
      )}

      {!loading && datos && sesionesFlat.length === 0 && (
        <div className="management-card text-center py-5">
          <i className="bi bi-calendar2-x display-5 text-muted mb-3 d-block" />
          <h5 className="fw-semibold mb-1">Sin sesiones en el período de esta promoción</h5>
          <p className="text-muted mb-0">Las sesiones se generan automáticamente al crear la promoción.</p>
        </div>
      )}

      {!loading && datos && sesionesFlat.length > 0 && (
        <div className="management-card overflow-auto" ref={scrollRef}>
          {/* Encabezado del calendario: meses + días */}
          <div style={{ display: 'flex', position: 'sticky', top: 0, background: '#fff', zIndex: 10, borderBottom: '2px solid #e5e7eb', marginBottom: 4 }}>
            {/* Celda vacía de la columna de horas */}
            <div style={{ width: timeColW, flexShrink: 0 }} />

            {grupos.map((g, gi) => (
              <div key={gi} style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Nombre del mes */}
                <div style={{
                  background: '#CB7B2E', color: '#fff',
                  textAlign: 'center', fontWeight: 700, fontSize: 11,
                  padding: '4px 0',
                  width: g.sesiones.length * (colWidth + colGap),
                }}>
                  {g.mes}
                </div>
                {/* Días del mes */}
                <div style={{ display: 'flex', gap: colGap }}>
                  {g.sesiones.map((s) => {
                    const realizada   = s.sesion_estado === 'realizada';
                    const suspendida  = s.sesion_estado === 'suspendida';
                    const bgCol = realizada  ? '#166534'
                                : suspendida ? '#7f1d1d'
                                : '#2C3E50';
                    return (
                      <div key={s.sesion_id} style={{
                        width: colWidth, textAlign: 'center',
                        background: bgCol, color: '#fff',
                        fontSize: 10, fontWeight: 700,
                        padding: '2px 2px 0',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 2,
                      }}>
                        <span>{s.lbl.dia} {s.lbl.num}</span>
                        {canEdit && (
                          <button
                            style={{
                              fontSize: 9, padding: '1px 4px', lineHeight: 1.2,
                              borderRadius: 3, border: 'none', cursor: 'pointer',
                              background: realizada ? '#bbf7d0' : '#e2e8f0',
                              color: realizada ? '#166534' : '#334155',
                              marginBottom: 2,
                            }}
                            title={realizada ? 'Marcar como programada' : 'Marcar como realizada'}
                            onClick={() => handleEstadoSesion(
                              s,
                              realizada ? 'programada' : 'realizada'
                            )}
                          >
                            {realizada ? '✓ realizada' : 'marcar ✓'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Grilla tipo calendar */}
          <div style={{ paddingTop: 4 }}>
            <CalendarioGrid
              sesiones={sesionesFlat}
              colores={colores}
              minHora={minHora}
              maxHora={maxHora}
              canEdit={canEdit}
              onClickVacio={abrirNuevo}
              onClickBloque={abrirEditar}
              onAsistencia={handleAsistencia}
              colWidth={colWidth}
              timeColW={timeColW}
              colGap={colGap}
              pxPorMin={pxPorMin}
            />
          </div>

          {/* Leyenda */}
          {leyenda.length > 0 && (
            <div className="mt-4 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
              <table style={{ fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#888', textAlign: 'left' }}>
                    <th style={{ padding: '2px 10px' }}></th>
                    <th style={{ padding: '2px 10px' }}>Materia</th>
                    <th style={{ padding: '2px 10px' }}>Docente</th>
                    <th style={{ padding: '2px 10px' }}>Modalidad</th>
                    <th style={{ padding: '2px 10px' }}>Horas (prog. / malla)</th>
                  </tr>
                </thead>
                <tbody>
                  {leyenda.map((m) => {
                    const horasProg = horasProgramadasPorMateria[m.id] || 0;
                    const horasMalla = horasMallaPorMateria[m.id];
                    const excede = horasMalla !== null && horasMalla !== undefined && horasProg > horasMalla;
                    return (
                      <tr key={m.id}>
                        <td style={{
                          background: m.color, width: 24, height: 20,
                          border: '1px solid #ddd',
                        }} />
                        <td style={{ padding: '2px 10px', fontWeight: 700 }}>{m.nombre}</td>
                        <td style={{ padding: '2px 10px', color: '#666' }}>{m.docente}</td>
                        <td style={{ padding: '2px 10px', color: '#666' }}>{m.modalidad}</td>
                        <td style={{ padding: '2px 10px', fontWeight: excede ? 700 : 400, color: excede ? '#C0392B' : '#666' }}>
                          {horasMalla !== null && horasMalla !== undefined ? `${horasProg}h / ${horasMalla}h` : `${horasProg}h`}
                          {excede && <i className="bi bi-exclamation-triangle-fill ms-1" title="Excede las horas definidas en Malla Curricular" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Alertas de conflicto de docente */}
      {warnings.length > 0 && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, maxWidth: 420 }}>
          {warnings.map((w, i) => (
            <div key={i} className="alert alert-warning alert-dismissible shadow mb-2 py-2 small"
              style={{ borderLeft: '4px solid #f59e0b' }}>
              <i className="bi bi-exclamation-triangle-fill me-2 text-warning" />
              <strong>Conflicto de docente:</strong> {w}
              <button type="button" className="btn-close btn-sm"
                onClick={() => setWarnings((p) => p.filter((_, j) => j !== i))} />
            </div>
          ))}
        </div>
      )}

      {/* Modal de bloque individual */}
      <BloqueModal
        open={modal}
        bloque={modalData?.bloque}
        sesion={modalData?.sesion}
        materias={materias}
        horasProgramadasPorMateria={horasProgramadasPorMateria}
        onClose={cerrarModal}
        onGuardar={handleGuardar}
        onEliminar={handleEliminar}
      />

      {/* Modal de asignación masiva */}
      <MasivoModal
        open={modalMasivo}
        sesiones={sesionesFlat}
        materias={materias}
        horasProgramadasPorMateria={horasProgramadasPorMateria}
        onClose={() => setModalMasivo(false)}
        onGuardar={handleGuardarMasivo}
      />

      {/* Modal de feriados/no laborables — solo consulta rápida desde acá, las
          fechas de las sesiones ya quedaron fijadas al crear la promoción. */}
      <InvitacionDocentesModal
        open={showInvitacionModal}
        promocionId={id}
        canEdit={canEdit}
        onClose={() => setShowInvitacionModal(false)}
      />

      <CalendarioNoLaborableCalendarModal
        open={showFeriadosModal}
        onClose={() => setShowFeriadosModal(false)}
      />
    </div>
  );
}
